import { and, eq } from "drizzle-orm";
import { format, getHours, getMinutes } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";

import { db } from "@/db/db.ts";
import { displayArtifact as displayArtifactTable, file as fileTable, transcription } from "@/db/schema.ts";
import { BroadcastAction, BroadcastRecipientList, ScheduleTrigger, WorkflowItem } from "@/db/types.ts";
import { parseCronScheduledDate } from "@/utils";

export type ScheduledWorkflowItem = {
  workspaceId: string;
  workflowItemId: string | null;
  feedIds: string[];
  scheduledCron?: string | undefined;
  timezone?: string | undefined;
  scheduledDate?: string | undefined;
};

export const calculateCron = (date: Date, dayOfWeek: number, timezone: string) => {
  const mins = getMinutes(date);
  const hours = getHours(date);
  const month = "*";
  const dayOfMonth = "?";
  const daysOfWeek = dayOfWeek.toString();
  const scheduledCron = `${mins} ${hours} ${dayOfMonth} ${month} ${daysOfWeek} *`;
  return {
    scheduledDate: "",
    scheduledCron,
    timezone,
  };
};

export const prettyCronDate = (scheduledTrigger, timezone) => {
  if (scheduledTrigger && timezone) {
    const hasCronSchedule = (scheduledTrigger as any)?.cronSchedule?.length > 0 && timezone?.length > 0;
    if (hasCronSchedule) {
      const parsedDate = parseCronScheduledDate((scheduledTrigger as any)?.cronSchedule.toString(), timezone);
      return formatInTimeZone(parsedDate, timezone || "", "'Every' iiii 'at' hh:mm aa");
    } else if (scheduledTrigger?.oneTimeSchedule !== null) {
      return format(new Date(scheduledTrigger?.oneTimeSchedule.toString()), "MMM dd, yyyy 'at' hh:mm aa");
    }
  }
};

export const calculateScheduledDateOrCron = ({
  date,
  dayOfWeek,
  doesRepeat,
}: {
  date: Date;
  dayOfWeek: number;
  doesRepeat: boolean;
}) => {
  if (!doesRepeat || typeof dayOfWeek !== "number" || isNaN(dayOfWeek)) {
    // date
    return date?.toISOString();
  }

  const mins = getMinutes(date);
  const hours = getHours(date);
  const month = "*";
  const dayOfMonth = "?";
  const daysOfWeek = dayOfWeek.toString();
  return `${mins} ${hours} ${dayOfMonth} ${month} ${daysOfWeek} *`;
};

export const fetchScheduledDate = (scheduledTrigger: ScheduleTrigger) => {
  if (scheduledTrigger?.cronSchedule && scheduledTrigger?.timezone) {
    return parseCronScheduledDate(scheduledTrigger.cronSchedule, scheduledTrigger.timezone);
  }
  if (scheduledTrigger?.oneTimeSchedule) {
    return new Date(scheduledTrigger.oneTimeSchedule);
  }
  return null;
};

export const transcriptionsQuery = (contentId: string) =>
  db.query.transcription.findMany({
    where: and(eq(transcription.contentId, contentId), eq(transcription.transcriptionType, "full")),
  });
export const rawFilesQuery = (contentId: string) =>
  db.query.file.findMany({
    where: eq(fileTable.contentId, contentId),
  });
export const displayArtifactQuery = (contentId: string) =>
  db.query.displayArtifact.findFirst({
    where: eq(displayArtifactTable.contentId, contentId),
  });

export const workflowMap = ({
  timezone,
  workflowItems,
  broadcasts,
  scheduleTriggers,
  broadcastRecipients,
}: {
  timezone: string;
  workflowItems: WorkflowItem[];
  broadcasts: BroadcastAction[];
  scheduleTriggers: ScheduleTrigger[];
  broadcastRecipients: BroadcastRecipientList[];
}) =>
  workflowItems
    ?.map((wi) => {
      const broadcast = broadcasts?.find((b) => b?.workflowItemId === wi?.id);
      const scheduledTrigger = broadcast?.id
        ? scheduleTriggers?.find((ots) => ots.broadcastActionId === broadcast?.id)
        : null;

      const recipients = broadcastRecipients
        ?.filter((br) => br.broadcastId === broadcast?.broadcastId)
        .map((br) => ({ id: br.feedId, title: br.title }));

      const maxRecipients = 10;
      const totalRecipients = recipients?.length ?? 0;
      const recipientMap =
        recipients
          ?.map((r) => r?.title)
          ?.filter((r) => r !== null)
          ?.slice(0, maxRecipients) ?? [];
      const andMore = totalRecipients > maxRecipients ? ` + ${totalRecipients - maxRecipients} more` : "";
      const prettyDate = prettyCronDate(scheduledTrigger, timezone);
      const scheduledDate = fetchScheduledDate(scheduledTrigger);

      return {
        ...wi,
        prettyDate,
        scheduledDate,
        broadcast,
        scheduledTrigger,
        recipients,
        recipientMap,
        andMore,
      };
    })
    ?.filter((w) => w.scheduledTrigger);

export const roundTimeToNearest15Minutes = (date: Date) => {
  const d = new Date(date);
  const minutes = d.getMinutes();
  const remainder = minutes % 15;

  if (remainder >= 7.5) {
    d.setMinutes(minutes + (15 - remainder));
  } else {
    d.setMinutes(minutes - remainder);
  }

  // Set seconds and milliseconds to 0 for a clean rounded time
  d.setSeconds(0);
  d.setMilliseconds(0);

  return d;
};

export const modalTabs = [
  { key: "media", text: "Add File", step: 1, active: false, disabled: false },
  { key: "generate", text: "Text", step: 1, active: false, disabled: false },
  { key: "date", text: "Date & Time", step: 2, active: false, disabled: false },
  { key: "channel", text: "Recipients", step: 3, active: false, disabled: false },
];
